import { supabase } from '../lib/supabase';

export interface Project {
  id: string;
  name: string;
  value: number;
  contact: string | null;
  plates: number | null;
  sale_date: string | null;
  created_at: string;
}

export interface Contract {
  id: string;
  customer_id: string;
  total_amount: number;
  created_at: string;
  customers: {
    id: string;
    full_name: string;
    cpf: string;
    phone: string | null;
    email: string | null;
  };
}

export interface DuplicateGroup {
  id: string;
  type: 'project' | 'contract';
  records: (Project | Contract)[];
  reason: string;
  matchScore: number;
}

export interface DuplicateFilters {
  matchByName: boolean;
  matchByValue: boolean;
  matchByContact: boolean;
  valueTolerance: number;
  nameMatchMode: 'exact' | 'similar';
}

class DuplicateDetectionService {
  private normalizeString(str: string): string {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s]/g, '')
      .trim();
  }

  private stringSimilarity(str1: string, str2: string): number {
    const s1 = this.normalizeString(str1);
    const s2 = this.normalizeString(str2);

    if (s1 === s2) return 1.0;

    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;

    if (longer.length === 0) return 1.0;

    const editDistance = this.levenshteinDistance(s1, s2);
    return (longer.length - editDistance) / longer.length;
  }

  private levenshteinDistance(str1: string, str2: string): number {
    const matrix: number[][] = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }

  private valueMatches(val1: number, val2: number, tolerance: number): boolean {
    const diff = Math.abs(val1 - val2);
    const percentDiff = (diff / Math.max(val1, val2)) * 100;
    return percentDiff <= tolerance;
  }

  async detectProjectDuplicates(filters: DuplicateFilters): Promise<DuplicateGroup[]> {
    const { data: projects, error } = await supabase
      .from('projects')
      .select('*')
      .order('created_at', { ascending: true });

    if (error || !projects) {
      throw new Error('Failed to fetch projects');
    }

    const duplicateGroups: Map<string, DuplicateGroup> = new Map();
    const processed: Set<string> = new Set();

    for (let i = 0; i < projects.length; i++) {
      if (processed.has(projects[i].id)) continue;

      const matches: Project[] = [projects[i]];
      const reasons: string[] = [];
      let totalScore = 0;

      for (let j = i + 1; j < projects.length; j++) {
        if (processed.has(projects[j].id)) continue;

        let score = 0;
        const currentReasons: string[] = [];

        if (filters.matchByName) {
          const nameSimilarity = this.stringSimilarity(
            projects[i].name,
            projects[j].name
          );

          if (filters.nameMatchMode === 'exact' && nameSimilarity === 1.0) {
            score += 0.4;
            currentReasons.push('nome idêntico');
          } else if (filters.nameMatchMode === 'similar' && nameSimilarity >= 0.8) {
            score += 0.4 * nameSimilarity;
            currentReasons.push(`nome similar (${(nameSimilarity * 100).toFixed(0)}%)`);
          }
        }

        if (filters.matchByValue && projects[i].value && projects[j].value) {
          if (this.valueMatches(projects[i].value, projects[j].value, filters.valueTolerance)) {
            score += 0.4;
            currentReasons.push('valor idêntico');
          }
        }

        if (filters.matchByContact && projects[i].contact && projects[j].contact) {
          const contactSimilarity = this.stringSimilarity(
            projects[i].contact,
            projects[j].contact
          );
          if (contactSimilarity >= 0.8) {
            score += 0.2;
            currentReasons.push('contato similar');
          }
        }

        if (score >= 0.5) {
          matches.push(projects[j]);
          processed.add(projects[j].id);
          reasons.push(...currentReasons);
          totalScore += score;
        }
      }

      if (matches.length > 1) {
        const avgScore = totalScore / (matches.length - 1);
        duplicateGroups.set(`project-${i}`, {
          id: `project-${i}`,
          type: 'project',
          records: matches,
          reason: [...new Set(reasons)].join(', '),
          matchScore: avgScore
        });
      }
    }

    return Array.from(duplicateGroups.values());
  }

  async detectContractDuplicates(filters: DuplicateFilters): Promise<DuplicateGroup[]> {
    const { data: contracts, error } = await supabase
      .from('financial_terms')
      .select(`
        *,
        customers (
          id,
          full_name,
          cpf,
          phone,
          email
        )
      `)
      .order('created_at', { ascending: true });

    if (error || !contracts) {
      throw new Error('Failed to fetch contracts');
    }

    const duplicateGroups: Map<string, DuplicateGroup> = new Map();
    const processed: Set<string> = new Set();

    for (let i = 0; i < contracts.length; i++) {
      if (processed.has(contracts[i].id)) continue;

      const matches: Contract[] = [contracts[i] as Contract];
      const reasons: string[] = [];
      let totalScore = 0;

      for (let j = i + 1; j < contracts.length; j++) {
        if (processed.has(contracts[j].id)) continue;

        let score = 0;
        const currentReasons: string[] = [];

        if (filters.matchByName && contracts[i].customers && contracts[j].customers) {
          const nameSimilarity = this.stringSimilarity(
            contracts[i].customers.full_name,
            contracts[j].customers.full_name
          );

          if (filters.nameMatchMode === 'exact' && nameSimilarity === 1.0) {
            score += 0.4;
            currentReasons.push('cliente idêntico');
          } else if (filters.nameMatchMode === 'similar' && nameSimilarity >= 0.8) {
            score += 0.4 * nameSimilarity;
            currentReasons.push(`cliente similar (${(nameSimilarity * 100).toFixed(0)}%)`);
          }
        }

        if (filters.matchByValue) {
          if (this.valueMatches(
            contracts[i].total_amount,
            contracts[j].total_amount,
            filters.valueTolerance
          )) {
            score += 0.4;
            currentReasons.push('valor idêntico');
          }
        }

        if (filters.matchByContact && contracts[i].customers && contracts[j].customers) {
          const phone1 = contracts[i].customers.phone || '';
          const phone2 = contracts[j].customers.phone || '';
          const email1 = contracts[i].customers.email || '';
          const email2 = contracts[j].customers.email || '';

          if (phone1 && phone2 && this.stringSimilarity(phone1, phone2) >= 0.8) {
            score += 0.2;
            currentReasons.push('telefone similar');
          }

          if (email1 && email2 && this.stringSimilarity(email1, email2) >= 0.8) {
            score += 0.2;
            currentReasons.push('email similar');
          }
        }

        if (score >= 0.5) {
          matches.push(contracts[j] as Contract);
          processed.add(contracts[j].id);
          reasons.push(...currentReasons);
          totalScore += score;
        }
      }

      if (matches.length > 1) {
        const avgScore = totalScore / (matches.length - 1);
        duplicateGroups.set(`contract-${i}`, {
          id: `contract-${i}`,
          type: 'contract',
          records: matches,
          reason: [...new Set(reasons)].join(', '),
          matchScore: avgScore
        });
      }
    }

    return Array.from(duplicateGroups.values());
  }

  async deleteDuplicates(
    recordIds: string[],
    recordType: 'project' | 'contract',
    reason: string,
    userId: string
  ): Promise<{ success: boolean; deletedCount: number; error?: string }> {
    const batchId = crypto.randomUUID();

    try {
      for (const recordId of recordIds) {
        let recordData: any = {};

        if (recordType === 'project') {
          const { data } = await supabase
            .from('projects')
            .select('*')
            .eq('id', recordId)
            .single();
          recordData = data;
        } else {
          const { data } = await supabase
            .from('financial_terms')
            .select('*, customers(*)')
            .eq('id', recordId)
            .single();
          recordData = data;
        }

        await supabase.from('duplicate_deletion_logs').insert({
          record_type: recordType,
          record_id: recordId,
          record_data: recordData,
          reason,
          deleted_by: userId,
          batch_id: batchId,
          metadata: {
            deleted_at: new Date().toISOString()
          }
        });

        if (recordType === 'project') {
          await supabase.from('projects').delete().eq('id', recordId);
        } else {
          const { data: financialTerm } = await supabase
            .from('financial_terms')
            .select('customer_id')
            .eq('id', recordId)
            .single();

          if (financialTerm?.customer_id) {
            // Delete related data for this specific contract
            await supabase.from('installments').delete().eq('financial_terms_id', recordId);

            // Delete the financial term first
            await supabase.from('financial_terms').delete().eq('id', recordId);

            // Check if customer has any other contracts
            const { data: remainingContracts } = await supabase
              .from('financial_terms')
              .select('id')
              .eq('customer_id', financialTerm.customer_id);

            // Only delete customer and related data if no other contracts exist
            if (!remainingContracts || remainingContracts.length === 0) {
              await supabase.from('technical_configs').delete().eq('customer_id', financialTerm.customer_id);
              await supabase.from('installation_locations').delete().eq('customer_id', financialTerm.customer_id);
              await supabase.from('customers').delete().eq('id', financialTerm.customer_id);
            }
          }
        }
      }

      return { success: true, deletedCount: recordIds.length };
    } catch (error) {
      return {
        success: false,
        deletedCount: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  async getAuditLogs(limit: number = 100) {
    const { data, error } = await supabase
      .from('duplicate_deletion_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      throw new Error('Failed to fetch audit logs');
    }

    return data || [];
  }
}

export const duplicateDetectionService = new DuplicateDetectionService();
